// The job in hand: which pod is selected, which one is actually started, and
// the ephemeral state of the check list. Check *results* live in Query — this
// only holds interaction state.

import type { StateCreator } from 'zustand';

export interface JobSlice {
  /** Index into the queue — what the operator is looking at. */
  activeIndex: number;
  /** The work order actually started, which survives browsing the queue. */
  startedWoId: number | null;
  startedMo: string | null;

  openCheck: number | null;
  /** Check ids whose instructions have been read (the sign-off gate). */
  readSet: number[];
  measureDraft: string;
  /**
   * Checks awaiting the server, so each card can show pending.
   *
   * A set, not one id: a photo takes seconds to land in Odoo, and the next
   * check should not have to wait for it. With a single slot the second
   * sign-off stole the first card's spinner and the first settling cleared
   * the second's, so a card could read idle while its write was still in
   * flight.
   */
  pendingCheckIds: number[];
  /** Reason the pod is paused, for the banner. */
  pauseReason: string | null;
  podCondensed: boolean;

  setActiveIndex: (i: number) => void;
  setStarted: (woId: number | null, mo: string | null) => void;
  toggleCheck: (id: number | null) => void;
  markRead: (id: number) => void;
  isRead: (id: number) => boolean;
  setMeasureDraft: (v: string) => void;
  setCheckPending: (id: number, pending: boolean) => void;
  setPauseReason: (r: string | null) => void;
  setPodCondensed: (v: boolean) => void;
  resetJob: () => void;
}

export const createJobSlice: StateCreator<JobSlice, [], [], JobSlice> = (set, get) => ({
  activeIndex: 0,
  startedWoId: null,
  startedMo: null,
  openCheck: null,
  readSet: [],
  measureDraft: '',
  pendingCheckIds: [],
  pauseReason: null,
  podCondensed: false,

  setActiveIndex: (activeIndex) =>
    set((s) =>
      // Returning to the running job keeps its state; moving to a different pod
      // clears the check-list interaction state.
      s.activeIndex === activeIndex
        ? { activeIndex }
        : { activeIndex, openCheck: null, readSet: [], measureDraft: '', pendingCheckIds: [] },
    ),
  setStarted: (startedWoId, startedMo) => set({ startedWoId, startedMo }),
  toggleCheck: (id) =>
    set((s) => ({ openCheck: s.openCheck === id ? null : id, measureDraft: '' })),
  markRead: (id) => set((s) => (s.readSet.includes(id) ? s : { readSet: [...s.readSet, id] })),
  isRead: (id) => get().readSet.includes(id),
  setMeasureDraft: (measureDraft) => set({ measureDraft }),
  setCheckPending: (id, pending) =>
    set((s) => {
      const has = s.pendingCheckIds.includes(id);
      if (pending === has) return s;
      return { pendingCheckIds: pending ? [...s.pendingCheckIds, id] : s.pendingCheckIds.filter((x) => x !== id) };
    }),
  setPauseReason: (pauseReason) => set({ pauseReason }),
  setPodCondensed: (podCondensed) => set((s) => (s.podCondensed === podCondensed ? s : { podCondensed })),
  resetJob: () =>
    set({
      startedWoId: null,
      startedMo: null,
      openCheck: null,
      readSet: [],
      measureDraft: '',
      pendingCheckIds: [],
      pauseReason: null,
      podCondensed: false,
    }),
});
